/**
 * Client-side service for JOC Club email registration (#619).
 * Talks to /api/joc-club/email, which owns the Twilio Verify credentials
 * and the club_members collection.
 */

export interface ClubEmailStatus {
  email: string;
  emailVerified: boolean;
  emailConsent: boolean;
  language?: string;
}

export class ClubEmailRequestError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string) {
    super(code);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(init?: RequestInit): Promise<T> {
  const res = await fetch('/api/joc-club/email', {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ClubEmailRequestError(res.status, body.error || 'unexpected');
  }
  return res.json();
}

export async function getClubEmailStatus(): Promise<ClubEmailStatus> {
  return request<ClubEmailStatus>();
}

export async function registerClubEmail(email: string, consent: boolean): Promise<void> {
  await request({ method: 'POST', body: JSON.stringify({ email, consent }) });
}

export async function verifyClubEmailCode(code: string): Promise<void> {
  await request({ method: 'PUT', body: JSON.stringify({ code }) });
}

export async function updateClubProfile(prefs: {
  emailConsent?: boolean;
  language?: string;
}): Promise<ClubEmailStatus> {
  return request<ClubEmailStatus>({ method: 'PATCH', body: JSON.stringify(prefs) });
}
